/**
 * XML Utility Functions
 *
 * Shared utilities for handling XML data from API responses.
 */

/**
 * Extracts XML payload from various API response formats.
 * Handles:
 * 1. Raw XML string response
 * 2. Wrapped string in response.data
 * 3. Array structure with xmlData fields
 *
 * @param response - The API response (can be string, object with data, or nested structure)
 * @returns The extracted XML payload string
 * @throws Error if the response format is unexpected and cannot be processed
 */
export const extractXmlPayload = (response: unknown): string => {
  // Check if response is already a string (raw XML)
  if (typeof response === 'string') {
    return response;
  }

  // Check if response is an object
  if (response && typeof response === 'object') {
    const responseObj = response as Record<string, unknown>;

    // Check if response.data is a string
    if (typeof responseObj.data === 'string') {
      return responseObj.data;
    }

    // Extract from array structure
    const dataArray = (responseObj.data as Record<string, unknown>)?.data ?? responseObj.data ?? response;

    if (Array.isArray(dataArray)) {
      const xmlPayload = dataArray
        .map((item: { xmlData?: string }) => {
          if (typeof item?.xmlData === 'string') {
            return item.xmlData;
          }
          return '';
        })
        .filter(Boolean)
        .join('\n');

      if (xmlPayload) {
        return xmlPayload;
      }
    }
  }

  // Unexpected format - throw error to surface the issue
  throw new Error('Unexpected XML response format from server');
};

/**
 * Wraps XML payload in a root element if it doesn't already have proper XML structure.
 *
 * @param xmlPayload - The XML string to wrap
 * @param rootElement - The root element name to use (default: 'Accounts')
 * @returns The wrapped XML string
 */
export const wrapXmlIfNeeded = (xmlPayload: string, rootElement = 'Accounts'): string => {
  const trimmed = xmlPayload.trim();
  if (trimmed.startsWith('<')) {
    return xmlPayload;
  }
  return `<${rootElement}>${xmlPayload}</${rootElement}>`;
};
